import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    ArrowUpRight,
    CheckCircle2,
    ClipboardList,
    ExternalLink,
    HeartHandshake,
    MapPin,
    ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import CivicLawBanner from "../components/CivicLawBanner";

function isHttpOrHttpsUrl(url) {
    if (!url || typeof url !== "string") return false;
    try {
        const parsed = new URL(url.trim());
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
        return false;
    }
}

function JourneyForm({ task, authorityName, onSaved, onCancel }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        authority_name: authorityName || "",
        reference: "",
        note: "",
        next_follow_up_date: "",
    });

    const save = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const response = await api.post("/civic/journeys", {
                task_key: task.key,
                authority_name: form.authority_name || null,
                reference: form.reference || null,
                note: form.note || null,
                next_follow_up_date: form.next_follow_up_date || null,
            });
            toast.success("Private follow-up saved");
            onSaved(response.data);
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not save this follow-up");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={save} className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid={`civic-track-form-${task.key}`}>
            <div className="flex items-start gap-2 text-xs text-slate-600">
                <ShieldCheck size={14} className="shrink-0 mt-0.5" />
                <span>This is private to you. It records your follow-up only; it does not submit a form or assess eligibility.</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <Label htmlFor={`authority-${task.key}`}>Council or provider (optional)</Label>
                    <Input
                        id={`authority-${task.key}`}
                        value={form.authority_name}
                        onChange={(event) => setForm({ ...form, authority_name: event.target.value })}
                        placeholder="e.g. Buckinghamshire Council"
                    />
                </div>
                <div>
                    <Label htmlFor={`reference-${task.key}`}>Reference (optional)</Label>
                    <Input
                        id={`reference-${task.key}`}
                        value={form.reference}
                        onChange={(event) => setForm({ ...form, reference: event.target.value })}
                        placeholder="Reference from the official service"
                    />
                </div>
            </div>
            <div>
                <Label htmlFor={`followup-${task.key}`}>Follow-up date (optional)</Label>
                <Input
                    id={`followup-${task.key}`}
                    type="date"
                    value={form.next_follow_up_date}
                    onChange={(event) => setForm({ ...form, next_follow_up_date: event.target.value })}
                    className="max-w-xs"
                />
            </div>
            <div>
                <Label htmlFor={`note-${task.key}`}>What happened? (optional)</Label>
                <Textarea
                    id={`note-${task.key}`}
                    rows={2}
                    value={form.note}
                    onChange={(event) => setForm({ ...form, note: event.target.value })}
                    placeholder="For example, 'Called the council and was asked to upload a document'."
                />
            </div>
            <div className="flex gap-2">
                <Button type="submit" disabled={saving} className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white">
                    {saving ? "Saving…" : "Save private follow-up"}
                </Button>
                <Button type="button" variant="outline" onClick={onCancel} className="h-8 text-xs">
                    Cancel
                </Button>
            </div>
        </form>
    );
}

function JourneyCard({ journey, onUpdate }) {
    const [saving, setSaving] = useState(false);

    const setStatus = async (status) => {
        setSaving(true);
        try {
            const response = await api.put(`/civic/journeys/${journey.id}`, { status });
            onUpdate(response.data);
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not update this follow-up");
        } finally {
            setSaving(false);
        }
    };

    return (
        <li className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`civic-journey-${journey.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="font-medium text-slate-900">{journey.title}</div>
                    <div className="mt-1 text-xs text-slate-500">
                        {journey.authority_name || journey.source_name}
                        {journey.reference ? ` · ${journey.reference}` : ""}
                        {journey.next_follow_up_date ? ` · follow up ${new Date(journey.next_follow_up_date).toLocaleDateString()}` : ""}
                    </div>
                    {journey.note && <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{journey.note}</p>}
                </div>
                <select
                    aria-label={`Status for ${journey.title}`}
                    value={journey.status}
                    disabled={saving}
                    onChange={(event) => setStatus(event.target.value)}
                    className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700"
                >
                    <option value="not_started">Not started</option>
                    <option value="started">Started</option>
                    <option value="contacted">Contacted</option>
                    <option value="complete">Complete</option>
                </select>
            </div>
        </li>
    );
}

export default function LifeInArea() {
    const [catalogue, setCatalogue] = useState(null);
    const [journeys, setJourneys] = useState([]);
    const [postcode, setPostcode] = useState("");
    const [activeCategory, setActiveCategory] = useState("council_tax");
    const [trackingKey, setTrackingKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lookingUp, setLookingUp] = useState(false);

    const load = useCallback(async (postcodeValue = "") => {
        const params = postcodeValue.trim() ? { postcode: postcodeValue.trim() } : {};
        const [tasksResponse, journeysResponse] = await Promise.all([
            api.get("/civic/tasks", { params }),
            api.get("/civic/journeys"),
        ]);
        setCatalogue(tasksResponse.data);
        setJourneys(journeysResponse.data || []);
    }, []);

    useEffect(() => {
        load()
            .catch((error) => toast.error(error?.response?.data?.detail || "Could not load local help"))
            .finally(() => setLoading(false));
    }, [load]);

    const lookUpCouncil = async (event) => {
        event.preventDefault();
        setLookingUp(true);
        try {
            await load(postcode);
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not look up local services");
        } finally {
            setLookingUp(false);
        }
    };

    const tasks = useMemo(
        () => (catalogue?.tasks || []).filter((task) => task.category === activeCategory),
        [catalogue, activeCategory]
    );
    const authorityName = catalogue?.authority?.principal_name || catalogue?.authority?.name || "";

    const saveJourney = (journey) => {
        setJourneys((current) => [journey, ...current]);
        setTrackingKey(null);
    };
    const updateJourney = (journey) => {
        setJourneys((current) => current.map((entry) => (entry.id === journey.id ? journey : entry)));
    };

    if (loading) return <div className="p-8 text-sm text-slate-500" data-testid="life-in-area-loading">Loading local help…</div>;

    return (
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-8" data-testid="life-in-area-page">
            <header className="max-w-3xl">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                    <HeartHandshake size={14} /> Help for my home
                </div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">What do you need to do?</h1>
                <p className="text-sm text-slate-600 mt-2">Choose a task, use the official route, then keep a private follow-up record here if it helps.</p>
            </header>

            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-950" data-testid="life-emergency-banner">
                <strong>Life-threatening emergency?</strong> Call 999. This portal cannot raise the alarm or contact emergency services for you.
            </div>
            <CivicLawBanner />

            <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="council-lookup">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h2 className="font-heading text-lg font-semibold text-slate-900">Find the right council route</h2>
                        <p className="mt-1 text-sm text-slate-600">Enter a postcode to check whether we recognise the authority. If we do not, use the official council finder instead of relying on a guess.</p>
                    </div>
                    {catalogue?.council_finder?.url && (
                        <a
                            href={catalogue.council_finder.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2 text-slate-800"
                        >
                            Official council finder <ExternalLink size={13} />
                        </a>
                    )}
                </div>
                <form onSubmit={lookUpCouncil} className="mt-4 flex flex-wrap gap-2 items-end">
                    <div className="w-full sm:w-72">
                        <Label htmlFor="local-help-postcode">Postcode</Label>
                        <Input
                            id="local-help-postcode"
                            value={postcode}
                            onChange={(event) => setPostcode(event.target.value)}
                            placeholder="e.g. HP19 7AA"
                            data-testid="local-help-postcode"
                        />
                    </div>
                    <Button type="submit" disabled={lookingUp || !postcode.trim()} className="h-10 bg-slate-900 hover:bg-slate-800 text-white">
                        <MapPin size={15} className="mr-1.5" /> {lookingUp ? "Checking…" : "Find council"}
                    </Button>
                </form>
                {postcode.trim() && (
                    <p className="mt-3 text-sm text-slate-600" data-testid="local-help-authority-result">
                        {authorityName ? <>Matched <strong>{authorityName}</strong>. Confirm the service on the official site before submitting anything.</> : <>We could not verify an authority for this postcode here. Use the official council finder above; we will not guess your council.</>}
                    </p>
                )}
            </section>

            <section>
                <div className="flex gap-2 overflow-x-auto pb-1" aria-label="Local help categories">
                    {(catalogue?.categories || []).map((category) => (
                        <button
                            key={category.key}
                            type="button"
                            onClick={() => { setActiveCategory(category.key); setTrackingKey(null); }}
                            className={`shrink-0 rounded-full border px-3 py-1.5 text-sm ${activeCategory === category.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                            data-testid={`civic-category-${category.key}`}
                        >
                            {category.label}
                        </button>
                    ))}
                </div>
                <div className="mt-5 grid md:grid-cols-2 gap-4">
                    {tasks.map((task) => (
                        <article key={task.key} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`civic-task-${task.key}`}>
                            <h2 className="font-heading text-lg font-semibold text-slate-900">{task.title}</h2>
                            <p className="mt-2 text-sm text-slate-600">{task.why}</p>
                            <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">{task.next_step}</div>
                            <div className="mt-3 text-xs text-slate-500">{task.source} · checked {task.checked_on}</div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {task.in_app && <Link to={task.in_app} className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-300 px-3 text-xs font-medium text-slate-800 hover:bg-slate-50">Start a private record <ArrowUpRight size={13} /></Link>}
                                {isHttpOrHttpsUrl(task.official_url) && <a href={task.official_url} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center gap-1 rounded-md bg-slate-900 px-3 text-xs font-medium text-white hover:bg-slate-800">Open official route <ExternalLink size={13} /></a>}
                                <Button type="button" variant="outline" onClick={() => setTrackingKey(trackingKey === task.key ? null : task.key)} className="h-8 text-xs">
                                    <ClipboardList size={13} className="mr-1" /> Track follow-up
                                </Button>
                            </div>
                            {trackingKey === task.key && <JourneyForm task={task} authorityName={authorityName} onSaved={saveJourney} onCancel={() => setTrackingKey(null)} />}
                        </article>
                    ))}
                </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="civic-journeys">
                <div className="flex items-center gap-2">
                    <CheckCircle2 size={18} className="text-slate-700" />
                    <h2 className="font-heading text-lg font-semibold text-slate-900">My private follow-ups</h2>
                </div>
                <p className="mt-1 text-sm text-slate-600">These are visible only to you. They are not shared with the estate, council or provider.</p>
                {journeys.length === 0 ? (
                    <p className="mt-4 text-sm text-slate-500">No local-service follow-ups saved yet.</p>
                ) : (
                    <ul className="mt-4 space-y-3">{journeys.map((journey) => <JourneyCard key={journey.id} journey={journey} onUpdate={updateJourney} />)}</ul>
                )}
            </section>

            <p className="text-xs text-slate-500 max-w-3xl">{catalogue?.disclaimer}</p>
        </div>
    );
}
